import type { FileModel } from '@/models/gallery.model'
import { downloadFiles, DownloadFetchError } from '@/services/download.service'
import { getLogger } from '@/logger'

/** HTTP status that indicates a signed URL has expired and may be recoverable via a metadata refresh. */
const RETRYABLE_STATUS = 403

function isRetryableDownloadError(err: unknown): boolean {
  if (err instanceof DownloadFetchError) {
    return err.status === RETRYABLE_STATUS
  }
  if (err instanceof Error) {
    return err.message.includes(`(${RETRYABLE_STATUS})`) || err.message.includes(` ${RETRYABLE_STATUS}`)
  }
  return false
}

/** Result of a successful download, indicating whether a retry with refreshed files was needed. */
export interface DownloadWithRetryResult {
  retried: boolean
  files: FileModel[]
}

/** Parameters accepted by {@link downloadFilesWithRetry}. */
export interface DownloadWithRetryParams {
  files: FileModel[]
  galleryId: string
  /**
   * Invoked once if the initial download fails with a retryable (403)
   * error. Should refresh share metadata and return the re-mapped file
   * selection to retry with, or `null` if no refresh could be applied.
   */
  getRetryFiles: () => Promise<FileModel[] | null>
}

/**
 * Downloads the given files as a ZIP, retrying once with refreshed file
 * metadata when the initial attempt fails with an expired-signed-URL (403)
 * error.
 *
 * On any non-recoverable failure (non-403 error, refresh unavailable, or the
 * retry attempt itself failing), the *original* error is thrown so callers
 * can report the initial failure consistently.
 */
export async function downloadFilesWithRetry({
  files,
  galleryId,
  getRetryFiles,
}: DownloadWithRetryParams): Promise<DownloadWithRetryResult> {
  try {
    await downloadFiles(files, galleryId)
    return { retried: false, files }
  } catch (err) {
    if (!isRetryableDownloadError(err)) {
      throw err
    }

    let retryFiles: FileModel[] | null
    try {
      retryFiles = await getRetryFiles()
    } catch {
      retryFiles = null
    }
    if (!retryFiles) {
      throw err
    }

    try {
      await downloadFiles(retryFiles, galleryId)
      return { retried: true, files: retryFiles }
    } catch (retryErr) {
      getLogger().error('[goto-gallery] Download retry failed', retryErr)
      throw err
    }
  }
}
